import { AvailabilityReasonCodeDto, DepartmentDto, OperationAvailabilityDto } from "../../autogen/bff-api";

type OrgWithDepartments = { departments?: DepartmentDto[] };

const CREATE_SOURCING_EVENT = "CreateSourcingEvent";
const CREATE_PROJECT = "CreateProject";
const CREATE_CONTRACT = "CreateContract";
const CREATE_NEED = "CreateNeed";

const allows = (operations: OperationAvailabilityDto[] | undefined, operation: string): boolean =>
  operations?.some((o) => o.operation === operation && o.allowed) ?? false;

const departmentsAllowing = (org: OrgWithDepartments, operation: string): DepartmentDto[] =>
  (org.departments ?? []).filter((d) => d.active && allows(d.operations, operation));

const soleDepartmentAllowing = (org: OrgWithDepartments, operation: string): string | undefined => {
  const allowing = departmentsAllowing(org, operation);
  return allowing.length === 1 ? allowing[0].id : undefined;
};

export const departmentsForNewEvent = (org: OrgWithDepartments): DepartmentDto[] =>
  departmentsAllowing(org, CREATE_SOURCING_EVENT);

export const projectDepartmentOptions = (org: OrgWithDepartments): DepartmentDto[] =>
  departmentsAllowing(org, CREATE_PROJECT);

export const contractDepartmentOptions = (org: OrgWithDepartments): DepartmentDto[] =>
  departmentsAllowing(org, CREATE_CONTRACT);

export const offersContractCreation = (org: OrgWithDepartments): boolean =>
  (org.departments ?? []).some(
    (d) =>
      d.active &&
      d.operations?.some(
        (o) => o.operation === CREATE_CONTRACT && (o.allowed || o.reason?.code === AvailabilityReasonCodeDto.NotInPlan),
      ),
  );

export const mayCreateContractFromEvent = (org: OrgWithDepartments, homeDepartmentId: string | null | undefined) => {
  const departmentId = homeDepartmentId ?? org.departments?.find((d) => d.isDefault)?.id;
  return !!departmentId && (org.departments ?? []).some((d) => d.id === departmentId && allows(d.operations, CREATE_CONTRACT));
};

export const needDepartmentOptions = (org: OrgWithDepartments): DepartmentDto[] =>
  departmentsAllowing(org, CREATE_NEED);

export const initialProjectDepartmentId = (org: OrgWithDepartments): string | undefined =>
  soleDepartmentAllowing(org, CREATE_PROJECT);

export const initialContractDepartmentId = (org: OrgWithDepartments): string | undefined =>
  soleDepartmentAllowing(org, CREATE_CONTRACT);

export const initialNeedDepartmentId = (org: OrgWithDepartments): string | undefined =>
  soleDepartmentAllowing(org, CREATE_NEED);

export const departmentName = (org: OrgWithDepartments, departmentId: string | null | undefined) =>
  departmentId ? (org.departments ?? []).find((d) => d.id === departmentId)?.name : undefined;
